/* eslint-disable react/react-in-jsx-scope */
/*
 * File: ProductBulkInput.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 29 Nov 2021 2:50:21 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Modal } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { useFormikContext } from 'formik';
import lodash from 'lodash';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { FancyInputSearch } from './FancyInputSearch';

export const ProductBulkInput = (): JSX.Element => {
  const { values, setValues } = useFormikContext<SalesOrderPayloadModel>();

  const [inputValue, _setInputValue] = useState<string | undefined>(undefined);

  /* ****************** functions ****************** */
  const handleSearch = useCallback(
    (value: string): void => {
      const numberOfSpaces = (value.match(/ /g) || []).length;

      if (numberOfSpaces) {
        setValues((prevState) => ({
          ...prevState,
          lines: [
            ...prevState.lines,
            ...value.split(' ').map((id) => ({
              productNumber: id,
              siteId: values.siteId ?? undefined,
              warehouseId: values.warehouseId ?? undefined,
              category: '',
              customerLineNumber: '',
              vendorId: '',
              deliveryType: '',
              description: '',
              dispositionId: '',
              extendedPrice: 0,
              isImport: false,
              manufacturer: '',
              ownerId: '',
              quantity: 0,
              conditionId: '',
              unitCost: 0,
              unitPrice: 0,
              totalCost: 0,
              alternateItemId: null,
              isPosted: false,
              isCategoryValidated: false,
              isConditionValidated: false,
              isDeliveryTypeValidated: false,
              isDispositionValidated: false,
              isManufacturerValidated: false,
              isProductNumberValidated: false,
              isSiteValidated: false,
              isVendorValidated: false,
              isWarehouseValidated: false,
              isOwnerValidated: false,
              lineNumber: 1,
              id: lodash.uniqueId()
            }))
          ]
        }));
      }

      if (!numberOfSpaces && value.length) {
        setValues((prevState) => ({
          ...prevState,
          lines: [
            ...prevState.lines,
            {
              productNumber: value,
              siteId: values.siteId || '',
              warehouseId: values.warehouseId || '',
              category: '',
              customerLineNumber: '',
              vendorId: '',
              deliveryType: '',
              description: '',
              dispositionId: '',
              extendedPrice: 0,
              isImport: false,
              manufacturer: '',
              ownerId: '',
              quantity: 0,
              conditionId: '',
              unitCost: 0,
              unitPrice: 0,
              totalCost: 0,
              alternateItemId: null,
              isPosted: false,
              isCategoryValidated: false,
              isConditionValidated: false,
              isDeliveryTypeValidated: false,
              isDispositionValidated: false,
              isManufacturerValidated: false,
              isProductNumberValidated: false,
              isSiteValidated: false,
              isVendorValidated: false,
              isWarehouseValidated: false,
              isOwnerValidated: false,
              lineNumber: 1,
              id: lodash.uniqueId()
            }
          ]
        }));
      }

      _setInputValue(undefined);
    },
    [setValues, values.siteId, values.warehouseId]
  );

  const handleChange = (ev: ChangeEvent<HTMLInputElement>): void => {
    _setInputValue(ev.currentTarget.value);
  };

  /* ****************** Use Effects ****************** */
  // Handle displaying modal for multiple product numbers
  useEffect(() => {
    if (inputValue !== undefined && inputValue.length > 60) {
      Modal.confirm({
        title: 'Bulk Product Values',
        onCancel: () => {
          _setInputValue(undefined);
        },
        onOk: () => handleSearch(inputValue),
        content: <TextArea rows={20} value={inputValue.replaceAll(' ', '\n')} />
      });
    }
  }, [handleSearch, inputValue, values.siteId, values.warehouseId]);

  /* ****************** Render ****************** */
  return <FancyInputSearch value={inputValue} placeHolder="Product Id(s)" inputValue={inputValue} handleSearch={handleSearch} handleChange={handleChange} width={250} />;
};
